import {
  ELSCommonConfig,
  ELSStorageHelper
} from '@els/els-ui-common-react';
import {
  ADMIN_STATE_PERSIST_KEY,
  PLP_SOURCE_TYPE_LABELS,
  UNKNOWN_PLP_SOURCE_TYPE_LABEL,
  VALID_PLP_SOURCE_TYPE_FILTERS
} from './admin.constants';
import { RaasAssessmentSourceDto } from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { AdminComponentState } from './admin.models';

/**
 * Save admin filter state to session storage
 */
export const saveAdminFilters = (state: AdminComponentState): void => {
  try {
    const toStore = {
      ...state,
      createdAtFromInput: state.createdAtFromInput.toISOString(),
      createdAtToInput: state.createdAtToInput.toISOString(),
    };
    ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
      .namespace('')
      .put(ADMIN_STATE_PERSIST_KEY, toStore);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save admin filters to session storage:', error);
  }
};

/**
 * Load admin filter state from session storage and merge with default state
 */
export const loadAdminFilters = (defaultState: AdminComponentState): AdminComponentState => {
  try {
    const stored = ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
      .namespace('')
      .get(ADMIN_STATE_PERSIST_KEY);

    if (!stored) {
      return defaultState;
    }

    // Merge persisted filters with default state, converting date strings back to Date objects.
    // A persisted sourceTypeFilter is only honoured if it is still a recognised option, otherwise
    // the dropdown would render as "All" while the fetch silently applied a phantom filter.
    const storedSourceTypeIsValid = VALID_PLP_SOURCE_TYPE_FILTERS.includes(stored.sourceTypeFilter);

    return {
      ...defaultState,
      ...stored,
      sourceTypeFilter: storedSourceTypeIsValid ? stored.sourceTypeFilter : defaultState.sourceTypeFilter,
      createdAtFromInput: stored.createdAtFromInput ? new Date(stored.createdAtFromInput) : defaultState.createdAtFromInput,
      createdAtToInput: stored.createdAtToInput ? new Date(stored.createdAtToInput) : defaultState.createdAtToInput,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load admin filters from session storage:', error);
    return defaultState;
  }
};

/**
 * Clear admin filter state from session storage
 */
export const clearAdminFilters = (): void => {
  try {
    ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
      .namespace('')
      .remove(ADMIN_STATE_PERSIST_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to clear admin filters from session storage:', error);
  }
};

/**
 * Render the PLP Source Type cell as a comma-separated list of display labels,
 * one per source attached to the assessment. Empty when there are no sources.
 */
export const renderPlpSourceTypes = (sourceList: RaasAssessmentSourceDto[]): string => {
  return (sourceList || [])
    .map((source) => PLP_SOURCE_TYPE_LABELS[source.sourceType] || source.sourceType || UNKNOWN_PLP_SOURCE_TYPE_LABEL)
    .join(', ');
};
